<template>
  <section id="robot" class="hp-robot">
    <div class="hp-robot__container">
      <!-- 标题 -->
      <div class="hp-robot__head" data-aos="fade-up">
        <h2 class="hp-robot__title">机器人业务 · 智慧场景赋能</h2>
        <p class="hp-robot__subtitle">智能机器人赋能智慧场景，助力企业自动化升级</p>
      </div>

      <!-- 视频展示区域 - 21:9 超宽比例 -->
      <div class="hp-robot__stage" data-aos="fade-up">
        <div class="hp-robot__video-box">
          <!-- 参考 floor6.vue：不用 loop，靠 @ended 播完自动切下一个 -->
          <!-- showVideo 先卸载再重建，强制干净重载，避免旧 src 的播放状态残留 -->
          <!-- Vue 2 把 video 的 muted 当 DOM prop 处理，静态属性会被当成 falsy，必须写 :muted="true" -->
          <video
            v-if="showVideo"
            ref="video"
            :src="activeRobot.video"
            autoplay
            :muted="true"
            playsinline
            class="hp-robot__video"
            @canplay="onCanPlay"
            @timeupdate="onTimeUpdate"
            @ended="onVideoEnded"
            @error="onVideoError"
          ></video>

          <!-- 切换指示器 - 仅桌面端显示 -->
          <div class="hp-robot__dots">
            <button
              v-for="(item, i) in robots"
              :key="item.id"
              type="button"
              class="hp-robot__dot"
              :class="{ 'is-active': i === activeIndex }"
              :aria-label="`切换到第${i + 1}个机器人`"
              @click="handleCardClick(i)"
            ></button>
          </div>
        </div>
      </div>

      <!-- 机器人卡片 - 移动端并排展示，支持滑动切换 -->
      <div
        ref="container"
        class="hp-robot__cards" data-aos="fade-up"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <div
          v-for="(robot, i) in robots"
          :key="robot.id"
          data-robot-card
          class="hp-robot__card"
          @click="handleCardClick(i)"
        >
          <!-- 视频播放进度条 -->
          <div class="hp-robot__progress">
            <div
              class="hp-robot__progress-bar"
              :style="{ width: i === activeIndex ? `${progress}%` : '0%' }"
            ></div>
          </div>

          <!-- 型号 -->
          <div class="hp-robot__model">{{ robot.model }}</div>

          <!-- 名称 -->
          <h3 class="hp-robot__name">{{ robot.name }}</h3>

          <!-- 描述 -->
          <p class="hp-robot__desc">{{ robot.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// 对应 projects-vue 的 src/components/sections/RobotSection.vue
// 视频放在 public/homePage 下，不进 bundle；BASE_URL 兼容 VUE_APP_PUBLIC_PATH
const VIDEO_BASE = `${process.env.BASE_URL}homePage/`

const ROBOTS = [
  {
    id: 'lm200',
    model: 'LM200',
    name: 'AI智能零售巡检机器人',
    description:
      '专为线下无人零售店及中小型商超打造，整合自主导航、视觉识别与人机交互功能，覆盖导购、运营与巡检场景，提供轻量化智能零售解决方案。',
    video: `${VIDEO_BASE}4-LM200.webm`
  },
  {
    id: 'lm202',
    model: 'LM202',
    name: 'AI智能零售巡检机器人',
    description:
      '面向线下无人零售店和中大型商超，提供零售服务与运营管理解决方案，具备自主导航、AI视觉识别、人机交互等能力，覆盖导购、运营、巡检三大场景。',
    video: `${VIDEO_BASE}4-LM202.webm`
  },
  {
    id: 'lm203',
    model: 'LM203',
    name: 'AI智能安保巡逻机器人',
    description:
      '基于园区级L4低速无人驾驶技术打造，集成多传感器融合感知、厘米级高精度定位与AI智能识别算法，可在复杂环境中自主运行、智能巡检与主动安防。',
    video: `${VIDEO_BASE}4-LM203.webm`
  },
  {
    id: 'lm211',
    model: 'LM211',
    name: 'AI智能零售机器人',
    description:
      '具备货物分拣、货架整理等功能，可迎宾导购、人机互动替代人工重复作业，节约基础人力成本。',
    video: `${VIDEO_BASE}4-LM211.webm`
  }
]

export default {
  name: 'RobotSection',
  data() {
    return {
      robots: ROBOTS,
      activeIndex: 0,
      progress: 0,
      // 参考 floor6.vue：切换时先卸载 video 再在下一个 tick 重建
      showVideo: true
    }
  },
  computed: {
    activeRobot() {
      return this.robots[this.activeIndex]
    }
  },
  watch: {
    // 自动滚动到当前激活的卡片
    activeIndex() {
      this.$nextTick(this.scrollToActiveCard)
    }
  },
  mounted() {
    // 触摸滑动的起止坐标是非响应式的临时值，直接挂在 this 上
    this.touchStartX = 0
    this.touchEndX = 0

    // 不再用定时器轮播：切换时机由 video 的 ended 事件决定（参考 floor6.vue）
    this.scrollToActiveCard()
  },
  methods: {
    // 切到指定机器人：参考 floor6.vue 的 handleClick，销毁 video 再重建以强制重载
    switchTo(index) {
      this.activeIndex = index
      this.progress = 0
      this.showVideo = false
      this.$nextTick(() => {
        this.showVideo = true
      })
    },
    // 播完自动切下一个（参考 floor6.vue 的 onVideoEnded），取代原来固定 8 秒的定时器
    onVideoEnded() {
      this.switchTo((this.activeIndex + 1) % this.robots.length)
    },
    // 视频加载失败也要往下走，否则没有定时器兜底会永久停在这一屏
    onVideoError() {
      this.onVideoEnded()
    },
    // 参考 floor6.vue 的 onCanPlay：就绪后归零进度，并兜底触发一次播放
    onCanPlay(e) {
      this.progress = 0
      const played = e.target.play()
      if (played && played.catch) played.catch(() => {})
    },
    // 进度条：从事件对象取 video，避免重建间隙里 $refs.video 为空
    onTimeUpdate(e) {
      const video = e.target
      if (video.duration) {
        this.progress = (video.currentTime / video.duration) * 100
      }
    },
    scrollToActiveCard() {
      const container = this.$refs.container
      if (!container) return
      const cards = container.querySelectorAll('[data-robot-card]')
      const activeCard = cards[this.activeIndex]
      if (!activeCard) return
      if (container.scrollTo) {
        container.scrollTo({ left: activeCard.offsetLeft, behavior: 'smooth' })
      } else {
        container.scrollLeft = activeCard.offsetLeft
      }
    },
    // 触摸滑动切换
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
    },
    handleTouchMove(e) {
      this.touchEndX = e.touches[0].clientX
    },
    handleTouchEnd() {
      const diff = this.touchStartX - this.touchEndX
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          // 向左滑动 -> 下一个
          this.switchTo((this.activeIndex + 1) % this.robots.length)
        } else {
          // 向右滑动 -> 上一个
          this.switchTo((this.activeIndex - 1 + this.robots.length) % this.robots.length)
        }
      }
    },
    handleCardClick(index) {
      this.switchTo(index)
    }
  }
}
</script>

<style lang="less" scoped>
@import './vars.less';

.hp-robot {
  position: relative;
  .hp-section-padding();
  background: #fff;
}

.hp-robot__container {
  .hp-container();
}

// 标题
.hp-robot__head {
  text-align: center;
  margin-bottom: 48px;
}
.hp-robot__title {
  .hp-section-title();
}
.hp-robot__subtitle {
  .hp-section-subtitle();
  max-width: 768px;
}

// ---------- 视频区 ----------
.hp-robot__stage {
  margin-bottom: 48px;
}
.hp-robot__video-box {
  position: relative;
  width: 100%;
  background: #f5f5f7;
  border-radius: 16px;
  overflow: hidden;

  // 21:9
  &::before {
    content: '';
    display: block;
    padding-top: 42.8571%;
  }
}
.hp-robot__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

// 指示器（原 `hidden md:flex`，移动端隐藏）
.hp-robot__dots {
  display: none;

  @media (min-width: @screen-md) {
    display: flex;
    gap: 8px;
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
  }
}
.hp-robot__dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: width 0.3s ease, background-color 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
  }

  &.is-active {
    width: 32px;
    background: #fff;
  }
}

// ---------- 卡片 ----------
.hp-robot__cards {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;
  padding: 0 16px 8px;
  margin: 0 -16px;
  text-align: left;

  @media (min-width: @screen-md) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
    overflow: visible;
    padding: 0;
    margin: 0;
  }
  @media (min-width: @screen-lg) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
.hp-robot__card {
  cursor: pointer;
  padding: 16px;
  border-radius: 16px;
  background: #fff;
  transition: box-shadow 0.3s ease;
  flex-shrink: 0;
  width: 280px;
  scroll-snap-align: center;

  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }

  @media (min-width: @screen-md) {
    padding: 24px;
    width: auto;
  }
}
.hp-robot__progress {
  height: 4px;
  width: 100%;
  background: @hp-border;
  border-radius: 9999px;
  margin-bottom: 16px;
  overflow: hidden;
}
.hp-robot__progress-bar {
  height: 100%;
  background: @hp-blue;
  border-radius: 9999px;
  transition: width 0.3s ease;
}
.hp-robot__model {
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  color: @hp-gray;
}
.hp-robot__name {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  color: @hp-ink;
}
.hp-robot__desc {
  margin: 0;
  font-size: 14px;
  line-height: 1.625;
  color: @hp-gray;
}
</style>
